import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { BusinessUnitModel } from '@fi-app/business-unit/business-unit.model';
import { IAccessRightFilterCollection, ILightWeightArea, ILightWeightObject } from '@fi-shared/models';
import { DataFieldModel, getDataFieldsSaveData } from '@fi-app/data-field/data-field.model';
import { ObjectTitleService } from '@fi-app/utilities/fi-services/object-title.service';
import { ModuleType, ObjectTypes } from '@fi-shared/types';
import { NgModel } from '@angular/forms';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { BusinessUnitService, IBusinessUnitAccessData } from '@fi-app/business-unit/business-unit.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { ProfileEditComponent, ProfileImageAlignment } from '@fi-app/components/profile-edit/profile-edit.component';
import { DataFieldPropertyType } from '@fi-app/data-field/data-field-property-type';
import { DataFieldHelperService } from '@fi-app/data-field/data-field-helper.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { ContactModel } from '@fi-app/components/contact.model';
import { FiContactInformationTypes } from '@fi-shared/types/fi-contact-information-types';
import { getDefaultCountryId } from '@fi-app/core/country-id';
import { AddressModel } from '@fi-app/utilities/address/address.model';
import { FiAddressTypes } from '@fi-app/core/address-types';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { DataFieldSectionType } from '@fi-app/business-unit/data-field-section-type';

@Component({
    selector: 'fi-business-unit-edit-new-layout',
    templateUrl: './fi-business-unit-edit-new-layout.component.html',
    styleUrls: ['./fi-business-unit-edit-new-layout.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-business-unit-edit-new-layout' },
    standalone: false,
})
export class FiBusinessUnitEditNewLayoutComponent implements OnInit {
    @Input() businessUnit: BusinessUnitModel;
    @Input() areasAvailableForBusinessUnit: ILightWeightArea[];
    @Input() accessRightFilterCollection: IAccessRightFilterCollection;
    @Input() lightWeightClusters: ILightWeightObject[];
    @Input() lightWeightBusinessAreas: ILightWeightObject[];
    @Input() businessUnitDataFields: DataFieldModel[];
    @Input() showHeader: boolean = true;
    @Output() afterSave = new EventEmitter();
    @Input() goToListAfterSave: boolean = true;
    @ViewChild(ProfileEditComponent, { static: false }) profileEditComponent: ProfileEditComponent;

    selectedTabIndex: number = 0;
    pageHeader: string;
    ObjectTypes = ObjectTypes;
    ProfileImageAlignment = ProfileImageAlignment;
    DataFieldSectionType = DataFieldSectionType;
    private isActivatedInit: boolean = false;
    readonly qrCodeWidth: number = 232;

    constructor(
        private objectTitleService: ObjectTitleService,
        private loadingService: LoadingService,
        private businessUnitService: BusinessUnitService,
        private saveChangesWarningService: SaveChangesWarningService,
        private fiStateService: FiStateService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private dataFieldHelperService: DataFieldHelperService,
        private statisticService: StatisticService,
        private userDataService: FiUserDataService,
        private fiCustomerDataService: FiCustomerDataService,
    ) {}

    ngOnInit() {
        this.setPageHeader();
        this.setDataFieldsData();
        this.statisticService.Visit(ModuleType.Core, ObjectTypes.BusinessUnit, this.businessUnit.Id);
    }

    private setPageHeader() {
        this.pageHeader = this.objectTitleService.GetTitle(
            this.businessUnit.Id,
            this.businessUnit.Name,
            ObjectTypes.BusinessUnit,
        );
    }
    private setDataFieldsData() {
        this.isActivatedInit = this.businessUnit.IsActivated;

        const getModelData = () => {
            return {
                unit: this.businessUnit,
            };
        };

        if (!this.businessUnit.EmailAddressContacts.length) {
            const emailAddressContactToAdd = new ContactModel();
            emailAddressContactToAdd.ContactType = FiContactInformationTypes.WorkEmailAddress; // company email address
            emailAddressContactToAdd.IsDefault = true;
            this.businessUnit.EmailAddressContacts.push(emailAddressContactToAdd);
        }

        if (!this.businessUnit.PhoneNumberContacts.length) {
            const phoneNumberContactToAdd = new ContactModel();
            phoneNumberContactToAdd.ContactType = FiContactInformationTypes.CellPhoneNumber; // cell phone number
            phoneNumberContactToAdd.CountryId = getDefaultCountryId(this.userDataService.applicationLanguage);
            phoneNumberContactToAdd.IsDefault = true;
            this.businessUnit.PhoneNumberContacts.push(phoneNumberContactToAdd);
        }

        if (!this.businessUnit.Addresses.length) {
            const addressToAdd = new AddressModel();
            addressToAdd.AddressType = FiAddressTypes.HomeAddress; //HomeAddress
            addressToAdd.IsDefault = true;
            this.businessUnit.Addresses.push(addressToAdd);
        }

        const businessUnitAccessData: IBusinessUnitAccessData = this.businessUnitService.getBusinessUnitAccessData(
            this.businessUnit.Id,
            this.accessRightFilterCollection,
        );
        if (!businessUnitAccessData.isCentralAdmin) {
            // hide Data field with DataFieldPropertyType.Active
            const activatedDataFieldIdx: number = this.businessUnitDataFields.findIndex((dataField: DataFieldModel) => {
                return dataField.PropertyType === DataFieldPropertyType.Active;
            });
            if (activatedDataFieldIdx !== -1) {
                this.businessUnitDataFields.splice(activatedDataFieldIdx, 1);
            }
        }

        this.saveChangesWarningService.start(getModelData);
    }

    getDataFieldsBySectionType(sectionType: DataFieldSectionType) {
        return this.dataFieldHelperService.getDataFieldsBySectionType(this.businessUnitDataFields, sectionType);
    }

    back() {
        window.history.back();
    }

    save() {
        this.profileEditComponent.profileItemOnSave().then(() => {
            this.businessUnit.DataFieldValues = getDataFieldsSaveData(this.businessUnitDataFields);

            this.loadingService
                .trackPromise(this.businessUnitService.saveBusinessUnit(this.businessUnit))
                .then((response) => {
                    if (response) {
                        this.saveChangesWarningService.stop();
                        this.afterSave.emit(this.businessUnit);
                        if (this.goToListAfterSave) {
                            this.fiStateService.go(FiStateNames.BusinessUnit.List);
                        }
                    } else {
                        this.fiDialogsService.error({
                            messageText: this.appTextService.get('error_message.default_header'),
                        });
                    }
                });
        });
    }

    onValidateError(invalidModels: NgModel[]) {
        this.showErrorDialog();
        if (invalidModels.some((model) => model.name?.includes('area-select'))) {
            this.selectedTabIndex = 0;
            return;
        }
    }

    private showErrorDialog() {
        this.fiDialogsService.notify({
            messageText: this.appTextService.get('common.error_all_mandatory'),
            headerText: this.appTextService.get('common.warning'),
        });
    }

    isCheckPresenceEnabledForCustomer(): boolean {
        return this.fiCustomerDataService.customerData.ChecklistPresenceEnabled;
    }
}
